<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { closeLightbox, UImageLightbox } from '@dott/core';
import { useLogoutSubscription } from '@/shared/composables/useLogoutSubscription';
import { useDocumentTitle } from '@/shared/composables/usePageTitle';

import EmptyLayout from '@/shared/layouts/EmptyLayout.vue';
import MainLayout from '@/shared/layouts/MainLayout.vue';

import { en } from '@nuxt/ui/locale';

useLogoutSubscription();
useDocumentTitle();

const route = useRoute();
const isProgramsRoute = computed(() => route.name === 'Programs' || route.name === 'Program');
const layoutName = computed((): 'main' | 'empty' => (route.meta.layout === 'main' ? 'main' : 'empty'));

/**
 * The viewer is hosted outside `router-view`, so a navigation does not unmount
 * it and its state would survive into the next page — as a full-screen overlay
 * of an image that page never showed, holding the keyboard in its focus trap.
 * The case that matters is the cross-tab logout above: it lands on /login via
 * `router.replace`, deliberately without a reload.
 */
watch(() => route.fullPath, () => closeLightbox());

// Programs supports phone layouts; preserve the desktop viewport on other routes.
watch(isProgramsRoute, (responsive) => {
  document.querySelector('meta[name="viewport"]')?.setAttribute('content', responsive
    ? 'width=device-width, initial-scale=1.0'
    : 'width=1280, initial-scale=1.0');
}, { immediate: true });
</script>

<template>
  <UApp
    :locale="en"
    :toaster="{ progress: false }"
  >
    <UDashboardGroup
      unit="rem"
      storage="local"
      class="relative inset-auto h-svh"
      :class="[layoutName === 'main' ? 'bg-muted' : '', isProgramsRoute ? 'programs-layout min-w-0' : 'min-w-[1280px]']"
    >
      <MainLayout v-if="layoutName === 'main'" />
      <EmptyLayout v-else />
    </UDashboardGroup>

    <!-- Single host for the app-wide image lightbox — opened imperatively from
         anywhere (rich-text node views included) via `openLightboxFromImage`. -->
    <UImageLightbox />
  </UApp>
</template>
